import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * The pieces a detail page's reference rail is built from.
 *
 * A detail page has two jobs and they want different shapes. The left column
 * carries the work — where the stock sits, what is inbound, what to do about
 * it — and wants tables and actions. The right rail carries what the thing *is*
 * — its thresholds, its lead time, how fast it moves — and wants to be scanned,
 * not read. Splitting them means the reference figures stay on screen while the
 * left column changes tabs, which is when they are actually wanted: comparing a
 * consumption trend against the reorder point is impossible if the two live in
 * different tabs.
 *
 * These are deliberately small and unopinionated about data, so equipment and
 * the other registers can use the same vocabulary.
 */

/** A titled block in the rail. */
export function RailCard({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            {icon}
            {title}
          </h3>
          {action}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/**
 * Label / value rows separated by hairlines.
 *
 * `tone` colours the value where the value itself carries a judgement — stock
 * available to order reads differently from stock merely on order. Everything
 * else stays default, so the colour keeps meaning something.
 */
export type DetailListItem = {
  label: string;
  value: React.ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'destructive' | 'info';
  /** Hide the row entirely rather than printing a dash for absent data. */
  when?: boolean;
};

const TONES: Record<NonNullable<DetailListItem['tone']>, string> = {
  default: 'text-foreground',
  success: 'text-status-success-foreground',
  warning: 'text-status-warning-foreground',
  destructive: 'text-status-destructive-foreground',
  info: 'text-status-info-foreground',
};

export function DetailList({ items }: { items: DetailListItem[] }) {
  const rows = items.filter((i) => i.when !== false);
  if (rows.length === 0) return null;

  return (
    <dl className="divide-y">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex items-baseline justify-between gap-4 py-2.5 first:pt-0"
        >
          <dt className="text-sm text-muted-foreground">{row.label}</dt>
          <dd
            className={cn(
              'text-right text-sm font-medium tabular-nums',
              TONES[row.tone ?? 'default']
            )}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A few comparable quantities, each with a bar showing its share of the largest.
 *
 * Only for values measured the same way — days against days. A bar invites the
 * eye to compare lengths, so putting unlike units in one stack would be a lie
 * told in pixels.
 */
export function MetricBars({
  rows,
  unit,
  format,
}: {
  rows: { label: string; value: number; className?: string }[];
  unit: string;
  /** For values that carry their own notation — money, percentages. */
  format?: (value: number) => string;
}) {
  const max = Math.max(...rows.map((r) => r.value), 0);
  if (max <= 0) return null;

  const show = (value: number) =>
    format ? format(value) : `${value % 1 === 0 ? value : value.toFixed(1)} ${unit}`;

  // A single bar is always full width, so it encodes nothing — the number beside
  // it already said everything. Bars are for comparing, so below two rows this
  // falls back to plain label/value lines.
  if (rows.length < 2) {
    return <DetailList items={rows.map((r) => ({ label: r.label, value: show(r.value) }))} />;
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.label} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-muted-foreground">{row.label}</span>
            <span className="text-sm font-semibold tabular-nums">{show(row.value)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full', row.className ?? 'bg-primary')}
              style={{ width: `${Math.max((row.value / max) * 100, 2)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A short row of totals under one caption — three windows onto the same measure,
 * which is a comparison rather than three separate facts and so belongs in one
 * block rather than three cards.
 */
export function StatStrip({
  caption,
  stats,
}: {
  caption?: string;
  stats: { label: string; value: React.ReactNode }[];
}) {
  return (
    <div className="space-y-2">
      {caption && (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {caption}
        </p>
      )}
      <div className="grid grid-cols-3 gap-2 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className="text-lg font-semibold tabular-nums">{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The rail itself: sticky on wide screens so it stays beside the work, and a
 * plain stack below `lg` where there is no second column to be beside.
 */
export function DetailLayout({ main, rail }: { main: React.ReactNode; rail: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="min-w-0 lg:col-span-2">{main}</div>
      <div className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:self-start">{rail}</div>
    </div>
  );
}
